import React from "react";
import type { TFunction } from "i18next";
import U2000InputCalendarCustom from "../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { NS } from "../../../U3105";
import type { U3105PayrollYearFormRow } from "../../../U3105Service";
import { U3105_MAX_PAYROLL_PERIODS, U3105_MIN_PAYROLL_PERIODS } from "../../../U3105Service";

interface U3105PayrollPeriodsSectionProps {
  formStateHandlers: U2000FormStateHandlers<U3105PayrollYearFormRow>;
  t: TFunction;
}

const U3105PayrollPeriodsSection: React.FC<U3105PayrollPeriodsSectionProps> = ({ formStateHandlers, t }) => {
  const data = formStateHandlers.getData();
  const rows = data?.payrollPeriods ?? [];
  const formState = formStateHandlers.getFormState();
  const canEditStructure = formState === U2000FormStateEnumState.EDIT;

  const addPeriod = () => {
    const current = formStateHandlers.getData();
    if (!current || current.payrollPeriods.length >= U3105_MAX_PAYROLL_PERIODS) return;
    const nextN = current.payrollPeriods.length + 1;
    formStateHandlers.setData({
      ...current,
      payrollPeriods: [
        ...current.payrollPeriods,
        { periodNumber: nextN, startAt: "", endAt: "" },
      ],
    });
  };

  const removePeriodAt = (index: number) => {
    const current = formStateHandlers.getData();
    if (!current || current.payrollPeriods.length <= U3105_MIN_PAYROLL_PERIODS) return;
    const next = current.payrollPeriods.filter((_, i) => i !== index).map((p, i) => ({
      ...p,
      periodNumber: i + 1,
    }));
    formStateHandlers.setData({
      ...current,
      payrollPeriods: next,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">{t("lblSectionPeriods", { ns: NS })}</h3>
        {canEditStructure && (
          <button
            type="button"
            className="rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={rows.length >= U3105_MAX_PAYROLL_PERIODS}
            onClick={addPeriod}
          >
            {t("btnAddPeriod", { ns: NS })}
          </button>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblPeriodNumber", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblPeriodStart", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblPeriodEnd", { ns: NS })}</th>
              {canEditStructure && <th className="w-24 px-3 py-2 text-center font-medium text-gray-700" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map((p, i) => (
              <tr key={`${p.periodNumber}-${i}`}>
                <td className="whitespace-nowrap px-3 py-2 align-middle text-gray-800">{p.periodNumber}</td>
                <td className="min-w-[200px] px-3 py-2 align-middle">
                  <div className="flex min-w-0 flex-col gap-1">
                    <U2000InputCalendarCustom
                      popupZIndex={9999}
                      formStateHandlers={formStateHandlers as never}
                      fieldName={`payrollPeriods.${i}.startAt`}
                      label=""
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>
                </td>
                <td className="min-w-[200px] px-3 py-2 align-middle">
                  <div className="flex min-w-0 flex-col gap-1">
                    <U2000InputCalendarCustom
                      popupZIndex={9999}
                      formStateHandlers={formStateHandlers as never}
                      fieldName={`payrollPeriods.${i}.endAt`}
                      label=""
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>
                </td>
                {canEditStructure && (
                  <td className="px-3 py-2 text-center align-middle">
                    <button
                      type="button"
                      className="text-sm font-medium text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={rows.length <= U3105_MIN_PAYROLL_PERIODS}
                      onClick={() => removePeriodAt(i)}
                    >
                      {t("btnRemovePeriod", { ns: NS })}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default U3105PayrollPeriodsSection;
